import CloseIcon from '@mui/icons-material/Close';
import { DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useEffect } from 'react';

import IconButton from '@/components/Buttons/IconButton';
import BootstrapDialog from '@/components/Dialog/BootstrapDialog';
import Warning from '@/images/WarningColored.svg';
import { useAnalytics } from '@/services/analytics';
import { StyleCollection } from '@/utils/types';
import styled from '@emotion/styled';
import Button from '../Buttons/Button';

interface ITexts {
    title: string;
    content: string;
    cta: string;
}

type Mode = 'circle' | 'square';

interface Props {
    open: boolean;
    isLoading: boolean;
    onDelete: () => Promise<void>;
    onClose: () => void;
    poster: string | undefined;
    text: ITexts;
    mode: Mode;
    itemType: string;
    showWarningIcon?: boolean;
}

const Infographic = styled.img`
    width: 2.4rem;
    height: 2.4rem;
    margin-inline-end: 1rem;
`;
const ImageContainer = styled.div<{ mode: Mode }>`
    width: ${({ mode }) => (mode === 'square' ? '100%' : 'auto')};
    height: ${({ mode }) => (mode === 'circle' ? '20rem' : 'auto')};
    aspect-ratio: ${({ mode }) => (mode === 'circle' ? '1/1' : '2/1')};

    min-height: 5rem;
    padding-top: 1.6rem;

    & img {
        border-radius: ${({ mode }) => (mode === 'circle' ? '50%' : '8px')};
        object-fit: ${({ mode }) => (mode === 'circle' ? 'cover' : 'contain')};
    }
`;
const Image = styled.img`
    width: 100%;
    height: 100%;
`;

const styles: StyleCollection = {
    deleteButton: _theme => ({
        height: '4.4rem',
        padding: '0.5rem 2rem',
        fontSize: '1.6rem',
        alignItems: 'flex-center',
        flex: 1,
        borderRadius: '8px',
    }),
    closeIcon: theme => ({
        marginLeft: 'auto',
        color: theme.colors.primary,
    }),
    contentStyle: {
        flex: 1,
        overflow: 'auto',
        alignItems: 'center',
    },
    cancelButton: {
        height: '4.4rem',
        padding: '0.5rem 2rem',
        fontSize: '1.6rem',
        alignItems: 'flex-start',
        borderRadius: '8px',
        flex: 1,
    },
};

const DialogTitleStyled = styled(DialogTitle)`
    padding: 2.4rem 3rem 0 3rem !important;
`;

const DialogContentStyled = styled(DialogContent)`
    padding: 1.6rem 3rem 0 3rem !important;
    overflow: hidden !important;

    p {
        margin: 0 !important;
    }
`;

const DialogActionsStyled = styled(DialogActions)`
    padding: 1.6rem 3rem 2.4rem 3rem !important;
`;

const IconButtonStyled = styled(IconButton)`
    position: absolute;
    top: 1rem;
    right: 1rem;
`;

const DialogSubtitle = styled(Typography)`
    line-height: 2.4rem;
    width: 100%;
    margin-bottom: 2rem;
    & span {
        text-decoration: underline;
        font-weight: bold;
    }
`;

export default function ({ itemType, mode, text, open, onDelete, onClose, poster, isLoading, showWarningIcon = true }: Props) {
    const mixpanel = useAnalytics();

    useEffect(() => {
        if (open) {
            mixpanel?.track(`delete-${itemType}`, { event: 'view' });
        }
    }, [open]);

    return (
        <BootstrapDialog
            onClose={onClose}
            open={open}
            width="45.2rem"
            contentstyle={styles.contentStyle}
            PaperProps={{ style: { borderRadius: '12px' } }}>
            <DialogTitleStyled id="delete-image-modal">
                {showWarningIcon && <Infographic src={Warning} />}
                <Typography variant="h2">{text.title}</Typography>
                <IconButtonStyled ariaLabel="close" onClick={onClose} sx={styles.closeIcon}>
                    <CloseIcon color="secondary" />
                </IconButtonStyled>
            </DialogTitleStyled>
            <DialogContentStyled>
                <DialogSubtitle variant="body1">{text.content}</DialogSubtitle>
                {poster && (
                    <ImageContainer mode={mode}>
                        <Image src={poster} />
                    </ImageContainer>
                )}
            </DialogContentStyled>
            <DialogActionsStyled>
                <Button aria-label="cancel" sx={styles.cancelButton} onClick={onClose} variant="outlined" color="secondary">
                    Cancel
                </Button>
                <Button
                    onClick={async () => {
                        await onDelete();
                        onClose();
                    }}
                    aria-label="delete"
                    color="error"
                    sx={styles.deleteButton}
                    isLoading={isLoading}>
                    {text.cta}
                </Button>
            </DialogActionsStyled>
        </BootstrapDialog>
    );
}
